import {createContext, ReactNode, useContext, useEffect, useState} from "react";
import {useAuth} from "./AuthProvider";
import {concertoLocalStorage} from "@/utils/localStorage";

type Client = {
    id: number;
    identifier: string;
    displayName: string;
    url: URL;
};

async function fetchClients(): Promise<Client[]> {
    const options: RequestInit = {
        headers: {
            "X-Heimdall-Client-Id": window.oidcClientSettings.client_id,
        },
        mode: "cors",
    };

    const userSub = concertoLocalStorage.get("profile").sub;
    const url = `${window.heimdallApiUrl}/user-related-clients/${userSub}`;
    const response = await fetch(url, options);
    const responseJson = await response.json();
    return responseJson
        .map(
            (client: any) =>
                ({
                    id: client.id,
                    identifier: client.identifier,
                    displayName: client.display_name,
                    url: new URL(client.redirect_uris[0]),
                }) as Client,
        )
        .filter((c: Client) => c.identifier !== window.oidcClientSettings.client_id);
}

const Context = createContext(undefined);

export function useRelatedClients(): Client[] {
    return useContext(Context);
}

/**
 * Provides the list of Heimdall clients related to the current user.
 */
export const RelatedClientProvider = ({children}: {children: ReactNode}) => {
    const [clients, setClients] = useState<Client[]>([]);

    const auth = useAuth();

    useEffect(() => {
        if (!auth) return;
        fetchClients().then((clients: Client[]) => {
            setClients(clients);
        });
    }, [auth]);

    return <Context.Provider value={clients}>{children}</Context.Provider>;
};
